import { AccountsEntity } from '@/domain/entities/account';
import {
  CreateAccountPayload,
  FindAccountPayload,
  ResetMfaPayload,
  UpdateAccountsPayload,
  UpdateRequiredMfaPayload,
  ChangePasswordPayload,
  ChangeEmailPayload,
} from '@/domain/ports/account/payloads';
import { AccountsRepository } from '@/domain/ports/account/repositories';
import { HttpClientAdapter } from '@/graphql/http-client-adapter';
import { logError } from '@/appInsights';
import {
  FIND_ACCOUNT,
  GET_ACCOUNT_ITEMS,
  UPDATE_ACCOUNT,
  FIND_PERMISSION,
  CREATE_ACCOUNT,
  RESET_MFA,
  UPDATE_REQUIRED_MFA,
  CHANGE_PASSWORD,
  CHANGE_EMAIL,
} from '../../graphql';

export class AccountRepoIml implements AccountsRepository {
  async resetMfa(payload: ResetMfaPayload): Promise<AccountsEntity> {
    const response = await HttpClientAdapter.mutate<AccountsEntity>({
      mutation: RESET_MFA,
      variables: { id: payload.id },
    });
    return response;
  }
  async updateRequiredMfa(payload: UpdateRequiredMfaPayload): Promise<AccountsEntity> {
    const response = await HttpClientAdapter.mutate<AccountsEntity>({
      mutation: UPDATE_REQUIRED_MFA,
      variables: { payload },
    });
    return response;
  }
  async get(payload?: FindAccountPayload): Promise<{ data: AccountsEntity[] }> {
    const { lastLoggedInFrom, lastLoggedInTo, ...rest } = payload ?? {};
    const hasFrom = lastLoggedInFrom != null;
    const hasTo = lastLoggedInTo != null;

    // Invariant: API expects both bounds or neither. UI sets both together; log if violated.
    if (hasFrom !== hasTo) {
      logError('Partial lastLoggedIn date range ignored — both bounds required', {
        lastLoggedInFrom,
        lastLoggedInTo,
      });
    }

    const query = {
      ...rest,
      ...(hasFrom && hasTo ? { lastLoggedInFrom, lastLoggedInTo } : {}),
    };

    const response: AccountsEntity[] = await HttpClientAdapter.query<AccountsEntity[]>({
      query: GET_ACCOUNT_ITEMS,
      dataKey: 'User_getList',
      variables: { query },
    });
    return {
      data: response,
    };
  }

  async update(payload: UpdateAccountsPayload): Promise<AccountsEntity> {
    const response = await HttpClientAdapter.mutate<AccountsEntity>({
      mutation: UPDATE_ACCOUNT,
      variables: { payload: { ...payload } },
    });
    return response;
  }

  async find(payload: string): Promise<{ dataDetail: AccountsEntity }> {
    const response: AccountsEntity = await HttpClientAdapter.query<AccountsEntity>({
      query: FIND_ACCOUNT,
      dataKey: 'User_getById',
      variables: { id: payload },
    });
    return {
      dataDetail: response,
    };
  }

  async getPermission(): Promise<string[]> {
    const response: string[] = await HttpClientAdapter.query<string[]>({
      query: FIND_PERMISSION,
      dataKey: 'User_getPermission',
    });
    return response;
  }

  async create(payload: CreateAccountPayload): Promise<AccountsEntity> {
    const response = await HttpClientAdapter.mutate<AccountsEntity>({
      mutation: CREATE_ACCOUNT,
      variables: { payload: { ...payload } },
    });
    return response;
  }

  async changePassword(payload: ChangePasswordPayload): Promise<AccountsEntity> {
    const response = await HttpClientAdapter.mutate<AccountsEntity>({
      mutation: CHANGE_PASSWORD,
      variables: { payload: { ...payload } },
    });
    return response;
  }

  async changeEmail(payload: ChangeEmailPayload): Promise<AccountsEntity> {
    const response = await HttpClientAdapter.mutate<AccountsEntity>({
      mutation: CHANGE_EMAIL,
      variables: { payload: { ...payload } },
    });
    return response;
  }
}
